/**
 * Passive-voice detection, shared by the in-editor proofread underlines and the
 * public /passive-voice-checker page.
 *
 * Deliberately a zero-import leaf: the marketing page ships this to the browser
 * and must not drag Tiptap or anything else in behind it.
 *
 * One definition on purpose. A free public checker that disagreed with the
 * underlines a writer sees after signing up would be worse than no checker.
 *
 * The heuristic is the standard one — a form of "to be" followed by a past
 * participle — which is what Hemingway and friends use. It is honest rather
 * than exact: it over-reports slightly, and the page says so, because passive
 * voice is frequently the right choice and a tool that scolds is a tool that
 * makes prose worse.
 */

/**
 * Words that can sit inside the verb phrase without ending it: negation and
 * adverbs, as in "had not been told" or "was clearly being paid". Capped at
 * two rather than unbounded — plenty for prose, and it keeps a starred group
 * next to another starred group from backtracking pathologically.
 */
const FILLER =
  "(?:\\s+(?:not|never|rarely|seldom|hardly|scarcely|already|always|often|just|still|now|then|also|only|\\w+ly)){0,2}";

/**
 * Auxiliary shapes that can head a passive.
 *
 * Split by branch because the auxiliary alone is not enough to tell voice
 * apart: only am/is/are/was/were can sit directly against a participle. "has
 * broken" and "had taken" are active perfects — they need "been" to turn
 * passive, and modals need "be". Collapsing these into one alternation is the
 * easy way to flag half the active sentences in a manuscript.
 *
 * Longest alternatives first: alternation is first-match, not longest-match,
 * so a bare "being" branch ahead of "was being" clips the highlight in half.
 */
const BE = `(?:${[
  `(?:am|is|are|was|were)${FILLER}(?:\\s+being)?`,
  `(?:has|have|had)${FILLER}\\s+been`,
  `(?:will|would|can|could|shall|should|may|might|must)${FILLER}\\s+(?:have\\s+been|be)`,
  `(?:be|been|being)`,
].join("|")})`;

/**
 * Irregular past participles. Regular ones are caught by `\w+ed`; a bare
 * `\w+en` would be far too greedy ("was green", "was often").
 */
const IRREGULAR = [
  "beaten", "become", "begun", "bent", "bitten", "bled", "blown", "born", "borne",
  "bought", "bound", "broken", "brought", "built", "burnt", "caught", "chosen",
  "come", "cost", "cut", "dealt", "done", "drawn", "driven", "drunk", "eaten",
  "fallen", "fed", "felt", "fought", "found", "flown", "forbidden", "forgiven",
  "forgotten", "frozen", "given", "gone", "ground", "grown", "heard", "held",
  "hidden", "hit", "hung", "hurt", "kept", "known", "laid", "lain", "led",
  "left", "lent", "let", "lost", "made", "meant", "met", "paid", "put", "read",
  "ridden", "risen", "run", "rung", "said", "seen", "sent", "set", "sewn",
  "shaken", "shed", "shone", "shot", "shown", "shut", "slept", "slid", "sold",
  "sown", "spent", "spilt", "split", "spoken", "spread", "sprung", "stolen",
  "stood", "struck", "stuck", "stung", "sung", "sunk", "sat", "swept", "swum",
  "swung", "sworn", "taken", "taught", "thrown", "told", "torn", "thought",
  "understood", "upset", "woken", "won", "worn", "wound", "written",
];

/**
 * Past participles that are almost always adjectival after "to be" — "she was
 * tired", not "she was tired by". Excluded to avoid the false positives that
 * make a checker look amateurish. The cost is missing the rare genuine passive
 * ("was tired by the journey"), which is a trade worth making.
 */
const ADJECTIVAL = [
  "annoyed", "based", "bored", "concerned", "confused", "determined", "disappointed",
  "dressed", "embarrassed", "excited", "finished", "frustrated", "gone", "interested",
  "involved", "located", "married", "pleased", "prepared", "relaxed", "satisfied",
  "scared", "situated", "supposed", "tired", "used", "worried",
];

const PARTICIPLE = `(?:${IRREGULAR.join("|")}|\\w+ed)`;

/**
 * Matches a passive construction. Global + case-insensitive.
 *
 * Callers that reuse it across inputs must clone it or reset `lastIndex` —
 * `matchesInDoc` in ProofreadExtension already clones per text node.
 */
export const PASSIVE_VOICE_RE = new RegExp(
  `\\b${BE}${FILLER}\\s+(?!(?:${ADJECTIVAL.join("|")})\\b)${PARTICIPLE}\\b`,
  "gi",
);

export interface PassiveMatch {
  /** Character offset into the input. */
  index: number;
  /** The matched phrase, e.g. "was broken". */
  text: string;
}

/** Every passive construction in a block of text, in order. */
export function findPassiveVoice(text: string): PassiveMatch[] {
  const re = new RegExp(PASSIVE_VOICE_RE.source, PASSIVE_VOICE_RE.flags);
  const out: PassiveMatch[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    out.push({ index: m.index, text: m[0] });
    // Zero-length matches are impossible here, but guard against a hang anyway.
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  return out;
}

/** Split on sentence-ending punctuation, keeping offsets. */
function splitSentences(text: string): { start: number; end: number; text: string }[] {
  const out: { start: number; end: number; text: string }[] = [];
  const re = /[^.!?]+[.!?]*/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (!m[0].trim()) continue;
    out.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
  }
  return out;
}

export interface PassiveVoiceStats {
  words: number;
  sentences: number;
  /** Sentences containing at least one passive construction. */
  passiveSentences: number;
  /** passiveSentences / sentences, 0 when there are no sentences. */
  ratio: number;
  matches: PassiveMatch[];
}

/**
 * Counts for the checker's summary line.
 *
 * Reported per sentence rather than per match because that is the number
 * writing guides quote, and because two passives in one sentence is one
 * problem to fix, not two.
 */
export function passiveVoiceStats(text: string): PassiveVoiceStats {
  const matches = findPassiveVoice(text);
  const sentences = splitSentences(text);
  const passiveSentences = sentences.filter((s) =>
    matches.some((m) => m.index >= s.start && m.index < s.end),
  ).length;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  return {
    words,
    sentences: sentences.length,
    passiveSentences,
    ratio: sentences.length ? passiveSentences / sentences.length : 0,
    matches,
  };
}
